'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import PremiumPNG from '@/assets/images/premium.png';
import { Icon } from '@/shared/ui/icon';
import './user-account.scss';

type Props = {
    user: {
        profilePictureUrl?: string;
        firstName: string;
        lastName: string;
        isPremiumMember: boolean;
        isVerified: boolean;
    };
    onClickEdit?: () => void;
    isLoading?: boolean;
    onClickAvatar?: () => void;
    onRemoveAvatar?: () => void;
    inputRef?: React.Ref<HTMLInputElement>;
    onFileChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};
export const UserAccount = ({
    user,
    onClickEdit,
    isLoading,
    onClickAvatar,
    onRemoveAvatar,
    inputRef,
    onFileChange,
}: Props) => {
    const t = useTranslations();

    const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();

    return (
        <div className="user-account">
            <div className="user-account__avatar-wrapper">
                <div className="user-account__avatar">
                    {isLoading ? (
                        <div
                            className="user-account__avatar-loading"
                            data-testid="user-account-avatar-loading"
                        >
                            <Icon name="loading" />
                        </div>
                    ) : user.profilePictureUrl ? (
                        <Image
                            src={user.profilePictureUrl}
                            alt={`${user.firstName} ${user.lastName}`}
                            fill
                            className="user-account__avatar-image"
                            data-testid="user-account-avatar-image"
                        />
                    ) : (
                        <div
                            className="user-account__avatar-initials"
                            data-testid="user-account-avatar-initials"
                        >
                            {initials}
                        </div>
                    )}
                    {onClickAvatar && (
                        <button
                            type="button"
                            className="user-account__avatar-edit"
                            onClick={onClickAvatar}
                            disabled={isLoading}
                            aria-label={t('USER_ACCOUNT.UPLOAD_AVATAR')}
                            data-testid="user-account-avatar-edit-button"
                        >
                            <Icon name="edit" />
                        </button>
                    )}
                </div>
                {onClickAvatar && (
                    <>
                        {user.profilePictureUrl && onRemoveAvatar && (
                            <button
                                type="button"
                                className="user-account__avatar-remove"
                                onClick={onRemoveAvatar}
                                disabled={isLoading}
                                aria-label={t('USER_ACCOUNT.REMOVE_AVATAR')}
                                data-testid="user-account-avatar-remove-button"
                            >
                                <Icon name="trash" />
                            </button>
                        )}
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            onChange={onFileChange}
                            hidden
                            data-testid="user-account-avatar-input"
                        />
                    </>
                )}
                {user.isPremiumMember && (
                    <div className="user-account__premium-badge">
                        <Image
                            src={PremiumPNG}
                            alt="Premium"
                            width={24}
                            height={24}
                            className="user-account__premium-icon"
                            data-testid="user-account-premium-icon"
                        />
                    </div>
                )}
            </div>
            <div className="user-account__info">
                <div className="user-account__name-wrapper">
                    <span className="user-account__name" data-testid="user-account-name">
                        {user.firstName} {user.lastName}
                    </span>
                    {user.isVerified && (
                        <div
                            className="user-account__verified-badge"
                            data-testid="user-account-verified-icon"
                        >
                            <Icon name="check" />
                        </div>
                    )}
                </div>
                {user.isPremiumMember && <p className="user-account__premium-label">Premium</p>}
                {onClickEdit && (
                    <button
                        className="user-account__edit-button"
                        type="button"
                        onClick={onClickEdit}
                        data-testid="user-account-edit-button"
                    >
                        {t('USER_ACCOUNT.EDIT')}
                        <Icon name="edit" />
                    </button>
                )}
            </div>
        </div>
    );
};
